import { NewsletterVariant, theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarScrollOverEffect, useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import { useMobileInlineAdSlotIsDisabledFlag } from "~/components/article/article-render/hooks";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { useContentSchemaRenderInlineWidget } from "~/components/content/content-schema-render/inline-widget/hooks";
import type { photoEssayArticleArticle$key } from "~/queries/__generated__/photoEssayArticleArticle.graphql";

import {
  AuthorDetail,
  Comment,
  Content,
  ContentFooterAdSlot,
  Follow,
  FooterAdSlot,
  HeaderAdSlot,
  LandingBanner,
  Left,
  PhotoEssayArticleContainer,
  PhotoEssayArticleWrapper,
  Social,
  SocialDesktop,
  SocialMobile,
  SummaryAndAuthor,
  Topic,
} from "./styles";
type Props = ArticleRenderChildProps & {
  reference: photoEssayArticleArticle$key;
};

export const PhotoEssayArticle: FunctionComponent<Props> = ({
  provider,
  reference: reference_,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const article = useFragment(
    graphql`
      fragment photoEssayArticleArticle on Article {
        ...articleLandingBannerArticle
        ...globalCheckerContent
      }
    `,
    reference_,
  );

  const { captureIntersectionElement } = useAppBarScrollOverEffect({
    swapTiming: "exit",
    swapVariant: "genericLight",
  });

  const { ComponentWithExtraProps: InlineWidgetWithExtraProps } =
    useContentSchemaRenderInlineWidget({
      newsletterVariant: NewsletterVariant.ArticleDark,
    });

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();

  return (
    <PhotoEssayArticleContainer>
      <PhotoEssayArticleWrapper>
        <LandingBanner ref={captureIntersectionElement} reference={article} variant="photo-essay" />
        <SummaryAndAuthor>
          {provider.subHeadline()}
          <Follow>{provider.topicFollowWidget({ variant: "banner" })}</Follow>
          {provider.authorBlock({ variant: "dark" })}
        </SummaryAndAuthor>

        <Content $variant="dark">
          {provider.body({
            extraComponentMap: {
              "inline-widget": InlineWidgetWithExtraProps,
            },
            inlineAdSlots: {
              "1": {
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "2": {
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "3": {
                adUnit: "m_lrec4d",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "4": {
                adUnit: "m_lrec4e",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "5": {
                adUnit: "m_lrec4f",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
            },
            paywallVariant: "dark",
            printHeadlineVariant: "dark",
          })}
        </Content>
        <Comment>{provider.commentTrigger()}</Comment>
        <Social>
          <SocialMobile>
            {provider.shareWidget({
              variant: "photoEssayMobile",
            })}
          </SocialMobile>
          <SocialDesktop>
            {provider.shareWidget({
              variant: "photoEssay",
            })}
          </SocialDesktop>
        </Social>
        <AuthorDetail>{provider.authorDetails({ variant: "dark" })}</AuthorDetail>
        <Topic>{provider.topics({ variant: "dark" })}</Topic>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          {provider.shareWidget({
            variant: "photoEssayLeft",
          })}
          {provider.actionWidget({ variant: "photoEssay" })}
        </Left>

        <HeaderAdSlot>
          {provider.adslot({
            adUnit: "d_lrec1",
            sizes: [[300, 250], "fluid"],
            withLabel: true,
          })}
        </HeaderAdSlot>

        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <ContentFooterAdSlot>
              {provider.adslot({
                adUnit: "native4",
                sizes: ["fluid"],
              })}
            </ContentFooterAdSlot>

            <FooterAdSlot>
              {provider.adslot({
                adUnit: "m_lrec3a",
                sizes: [[300, 250], "fluid"],
                withLabel: true,
              })}
            </FooterAdSlot>
          </GlobalChecker>
        </ClientSideSuspense>
      </PhotoEssayArticleWrapper>
    </PhotoEssayArticleContainer>
  );
};

PhotoEssayArticle.displayName = "PhotoEssayArticle";
